"use client";

import { createPortal } from "react-dom";
import { Loader2 } from "lucide-react";

import {
  Command,
  CommandGroup,
  CommandInput,
  CommandList,
} from "@/components/ui/command";
import NavSearchResultRow, {
  LoadingRows,
} from "@/components/nav/NavSearchResultRow";
import { NAV_ARRIVE, arriveStep } from "@/components/nav/nav-styles";
import { cn } from "@/lib/utils";

const GROUP_CLASS =
  "[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-3 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-[0.18em] [&_[cmdk-group-heading]]:!text-gray-400";

/**
 * The search input and the list under it — loading rows, the error, "No
 * matches found.", the grouped results and the "Updating results" footer —
 * driven by one `useNavSearch` instance. The pane beside the rail and the
 * small-screen dialog both render this; they differ only in what surrounds it.
 *
 * `inputSlot` is an element to render the input into instead of the top of
 * the list: the pane puts it in the top bar, right above the results. Keys
 * pressed there still reach the list, since
 * a portal keeps the React tree. `inputLeading` sits where the search icon
 * would, and `inputWrapperClassName` styles the row around both. Beside the
 * rail each group comes in after the one above it as the pane opens.
 */
export default function NavSearchResults({
  search,
  inputRef,
  apple,
  className,
  inputClassName,
  inputLeading,
  inputSlot,
  inputWrapperClassName,
  listClassName,
  onKeyDown,
}) {
  const { query, groupedResults, isLoading, hasResults, error } = search;
  const showLoadingRows = isLoading && !hasResults && Boolean(query.trim());

  const input = (
    <CommandInput
      ref={inputRef}
      value={query}
      onValueChange={search.setQuery}
      placeholder={search.placeholder}
      leading={inputLeading}
      wrapperClassName={inputWrapperClassName}
      className={cn("!text-gray-900 placeholder:!text-gray-400", inputClassName)}
    />
  );

  return (
    <Command
      shouldFilter={false}
      onKeyDown={onKeyDown}
      // The root and the list take focus by script only (tabIndex -1), so
      // neither draws the browser's ring.
      className={cn("bg-transparent !text-gray-900 outline-hidden", className)}
    >
      {inputSlot ? createPortal(input, inputSlot) : input}
      <CommandList className={cn("overflow-y-auto outline-hidden", listClassName)}>
        {showLoadingRows ? <LoadingRows /> : null}

        {!isLoading && error ? (
          <div className="px-6 py-10 text-center text-sm text-gray-500">
            {error}
          </div>
        ) : null}

        {!isLoading && !error && groupedResults.length === 0 ? (
          <div className="px-6 py-10 text-center text-sm text-gray-500">
            No matches found.
          </div>
        ) : null}

        {groupedResults.map(([group, items], index) => (
          <CommandGroup
            key={group}
            heading={group}
            className={cn(NAV_ARRIVE, GROUP_CLASS)}
            style={arriveStep(index)}
          >
            {items.map((result) => (
              <NavSearchResultRow
                key={result.id}
                result={result}
                apple={apple}
                onSelect={() => search.select(result)}
              />
            ))}
          </CommandGroup>
        ))}

        {isLoading && hasResults ? (
          <div className="flex items-center gap-2 border-t border-gray-100 px-4 py-2 text-xs text-gray-500">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Updating results
          </div>
        ) : null}
      </CommandList>
    </Command>
  );
}
